import React, { useEffect, useState } from "react";
import { X, ShieldAlert, ShieldCheck } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Button from "../../components/Button/Button";
import OTPinput from "../../components/Input/OTPInput.jsx";
import Notification from "../../components/Notification/Notification.jsx";

/* ===================== SERVICES ===================== */
import { apiPost } from "../../apiService.js";

/* ===================== STYLES ===================== */
import styles from "./ToggleButton.module.scss";

function ToggleButton() {
  const [showEnablePopup, setShowEnablePopup] = useState(false);
  const [showDisablePopup, setShowDisablePopup] = useState(false);

  const [qrImage, setQrImage] = useState("");
  const [, setSecretCode] = useState("");
  const [otpCode, setOtpCode] = useState("");

  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const [mfaEnabled, setMfaEnabled] = useState(false);

  // Checks current MFA status on load
  const checkMFAStatus = async () => {
    try {
      await apiPost("user", "/2fa/setup", {});
      setMfaEnabled(false);
    } catch (error) {
      if (
        error?.message?.includes("already enabled") ||
        error?.message?.includes("MFA already enabled")
      ) {
        setMfaEnabled(true);
      }
    }
  };

  useEffect(() => {
    checkMFAStatus();
  }, []);

  // Close modals on ESC
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setShowEnablePopup(false);
        setShowDisablePopup(false);
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("keydown", handleEsc);
    };
  }, []);

  const handleButtonClick = async () => {
    if (!mfaEnabled) {
      try {
        const res = await apiPost("user", "/2fa/setup", {});
        const qr = res?.data?.qrCode;
        const secret = res?.data?.secret;

        setSecretCode(secret || "");

        if (qr?.startsWith("data:image")) {
          setQrImage(qr);
        } else {
          setQrImage(`data:image/png;base64,${qr}`);
        }

        setShowEnablePopup(true);
      } catch (error) {
        setNotification({
          show: true,
          message: "2FA already enabled",
          type: "error",
        });
        setTimeout(() => setNotification({ show: false }), 3000);
      }
    } else {
      setShowDisablePopup(true);
    }
  };

  const handleEnableOk = async () => {
    if (!otpCode) {
      setNotification({
        show: true,
        message: "Please enter OTP code",
        type: "error",
      });
      setTimeout(() => setNotification({ show: false }), 3000);
      return;
    }

    try {
      const payload = { token: otpCode };
      const res = await apiPost("user", "/2fa/verify", payload);

      if (
        res?.success === true ||
        res?.status === "success" ||
        res?.message?.toLowerCase().includes("enabled")
      ) {
        setMfaEnabled(true);
        setShowEnablePopup(false);
        setOtpCode("");
        localStorage.setItem("mfaEnabled", "true");
        setNotification({
          show: true,
          message: "MFA enabled successfully",
          type: "success",
        });
        setTimeout(() => setNotification({ show: false }), 3000);
      }
    } catch (error) {
      setNotification({
        show: true,
        message: "Invalid MFA code",
        type: "error",
      });
      setTimeout(() => setNotification({ show: false }), 3000);
    }
  };

  const handleDisableOk = async () => {
    try {
      await apiPost("user", "/2fa/disable", {});

      setShowDisablePopup(false);
      setMfaEnabled(false);
      localStorage.removeItem("mfaEnabled");
      setNotification({
        show: true,
        message: "MFA disabled successfully",
        type: "success",
      });
      setTimeout(() => setNotification({ show: false }), 3000);
    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to disable MFA",
        type: "error",
      });
      setTimeout(() => setNotification({ show: false }), 3000);
    }
  };

  // Prevent closing when clicking inside the modal box
  const handleBoxClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div className={styles["admin-toggle-btn__wrapper"]}>
      <Button
        text={mfaEnabled ? "Disable MFA" : "Enable MFA"}
        icon={mfaEnabled ? ShieldAlert : ShieldCheck}
        className={
          mfaEnabled ? styles["admin-toggle-btn__trigger--danger"] : ""
        }
        onClick={handleButtonClick}
      />

      {/* ENABLE MFA POPUP */}
      {showEnablePopup && (
        <div
          className={styles["admin-toggle-btn__overlay"]}
          onClick={() => setShowEnablePopup(false)}
        >
          <div
            className={styles["admin-toggle-btn__box"]}
            onClick={handleBoxClick}
          >
            <div className={styles["admin-toggle-btn__header"]}>
              <h3 className={styles["admin-toggle-btn__title"]}>
                Multi-Factor Authentication
              </h3>
              <button
                className={styles["admin-toggle-btn__close-icon"]}
                onClick={() => setShowEnablePopup(false)}
              >
                <X size={20} strokeWidth={2.5} />
              </button>
            </div>

            <div className={styles["admin-toggle-btn__qr-wrapper"]}>
              <img
                src={qrImage}
                className={styles["admin-toggle-btn__qr-image"]}
                alt="QR Code"
              />
            </div>

            <div className={styles["admin-toggle-btn__instructions"]}>
              <p className={styles["admin-toggle-btn__text"]}>
                Scan QR using Google Authenticator or Authy
              </p>
              <span className={styles["admin-toggle-btn__otp-label"]}>
                Enter 6-digit confirmation code:
              </span>
            </div>

            <div className={styles["admin-toggle-btn__otp-wrapper"]}>
              <OTPinput onChange={(val) => setOtpCode(val)} />
            </div>

            <div className={styles["admin-toggle-btn__actions"]}>
              <Button
                text="Verify & Enable"
                onClick={handleEnableOk}
                style={{ width: "100%" }}
              />
            </div>
          </div>
        </div>
      )}

      {/* DISABLE CONFIRM POPUP */}
      {showDisablePopup && (
        <div
          className={styles["admin-toggle-btn__overlay"]}
          onClick={() => setShowDisablePopup(false)}
        >
          <div
            className={styles["admin-toggle-btn__box"]}
            onClick={handleBoxClick}
          >
            <div className={styles["admin-toggle-btn__header"]}>
              <h3 className={styles["admin-toggle-btn__title"]}>
                Disable Authentication
              </h3>
              <button
                className={styles["admin-toggle-btn__close-icon"]}
                onClick={() => setShowDisablePopup(false)}
              >
                <X size={20} strokeWidth={2.5} />
              </button>
            </div>

            <div className={styles["admin-toggle-btn__warning-icon"]}>
              <ShieldAlert size={32} strokeWidth={2.5} />
            </div>

            <p className={styles["admin-toggle-btn__warning-text"]}>
              Are you sure you want to disable Two-Factor Authentication on your
              profile? This will reduce your account security.
            </p>

            <div className={styles["admin-toggle-btn__actions"]}>
              <Button
                text="Yes, Disable MFA"
                className={styles["admin-toggle-btn__trigger--danger"]}
                onClick={handleDisableOk}
                style={{ width: "100%" }}
              />
            </div>
          </div>
        </div>
      )}

      {/* GLOBAL TOAST NOTIFICATION */}
      {notification.show && (
        <div className={styles["admin-toggle-btn__toast-container"]}>
          <Notification
            message={notification.message}
            type={notification.type}
          />
        </div>
      )}
    </div>
  );
}

export default ToggleButton;
